import { auth } from "./firebase.ts";
import { Lead, Shipper, Consignee, BillTo, Commodity, LaraEmail, NonltlShipment } from "../types.ts";


// localStorage is a best-effort offline cache only. Large datasets (the
// non-floral workbook is >5MB combined) can exceed the browser quota; a
// failed cache write must NEVER discard fresh API data, and a stale copy
// is dropped so it can't shadow live data after a later fetch failure.
function safeCache(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
}

// Role vocabulary lives in ./roles so the Express server can use it without
// pulling the browser Firebase SDK into the server bundle. Re-exported here
// because most of the UI imports these from './lib/db'.
import { normalizeRole } from "./roles.ts";
export { normalizeRole } from "./roles.ts";
export type { UserRole, CRMUser } from "./roles.ts";
import type { UserRole, CRMUser } from "./roles.ts";

// Fallback logic for localStorage
function getLocalLeads(): Lead[] {
  const data = localStorage.getItem("leadtracker_leads");
  return data ? JSON.parse(data) : [];
}

function saveLocalLeads(leads: Lead[]) {
  safeCache("leadtracker_leads", leads);
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (!auth.currentUser) {
    try {
      await auth.authStateReady();
    } catch {
      /* ignore */
    }
  }
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken(false);
      headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      console.warn("Failed to retrieve cached Firebase Auth ID token, attempting force refresh:", e);
      try {
        const token = await auth.currentUser.getIdToken(true);
        headers['Authorization'] = `Bearer ${token}`;
      } catch (err) {
        console.error("Failed to retrieve fresh Firebase Auth ID token:", err);
      }
    }
  }
  return headers;
}

/**
 * Universal authenticated fetch helper that automatically ensures Firebase Auth
 * is initialized and retries once with a force-refreshed ID token if a 401 response is returned.
 */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const defaultHeaders = await getAuthHeaders();
  const options: RequestInit = {
    ...init,
    headers: {
      ...defaultHeaders,
      ...(init.headers || {}),
    },
  };

  let res = await fetch(url, options);

  if (res.status === 401 && auth.currentUser) {
    try {
      console.warn(`authedFetch: received 401 from ${url}. Force refreshing Firebase Auth token & retrying...`);
      const freshToken = await auth.currentUser.getIdToken(true);
      const retryHeaders = {
        ...(options.headers as Record<string, string>),
        'Authorization': `Bearer ${freshToken}`,
      };
      res = await fetch(url, { ...options, headers: retryHeaders });
    } catch (err) {
      console.error("authedFetch: token refresh retry failed:", err);
    }
  }

  return res;
}

/**
 * Downloads a protected lead document using the authenticated bearer token.
 */
export async function downloadLeadDocument(
  leadId: string,
  docId: string,
  fileName?: string
): Promise<void> {
  const res = await authedFetch(`/api/leads/${leadId}/documents/${docId}/download`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to download document (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "document";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
}


// 1. Leads Storage & Synchronization (PostgreSQL Backed)
export async function fetchLeadsFromFirestore(): Promise<Lead[]> {
  try {
    if (!auth.currentUser) {
      await auth.authStateReady().catch(() => {});
    }
    const isLocalDev =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    if (!auth.currentUser && !isLocalDev) {
      return getLocalLeads();
    }
    const res = await authedFetch("/api/leads");
    if (!res.ok) {
      throw new Error(`Failed to fetch leads: ${res.statusText}`);
    }
    const leads: Lead[] = await res.json();
    if (leads.length > 0) {
      saveLocalLeads(leads);
      return leads;
    }
    return getLocalLeads();
  } catch (error) {
    if (auth.currentUser) {
      console.warn("PostgreSQL API fetch error, falling back to local storage:", error);
    }
    return getLocalLeads();
  }
}

/** Reads an { error } body if the server sent one, for a usable message. */
async function describeFailure(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (body?.error) return String(body.error);
  } catch {
    /* not JSON */
  }
  return `${fallback} (${res.status} ${res.statusText})`;
}

/**
 * Persists a lead. The local cache is still updated so an offline edit isn't
 * lost, but the error is RETHROWN: previously any failure (including a 403 or a
 * dropped connection) was swallowed with a console warning, so the UI reported
 * success for a write the database never received.
 */
export async function saveLeadToFirestore(lead: Lead): Promise<void> {
  // Cache first so the edit survives even if the request fails.
  const local = getLocalLeads();
  const idx = local.findIndex(l => l.id === lead.id);
  if (idx > -1) {
    local[idx] = lead;
  } else {
    local.push(lead);
  }
  saveLocalLeads(local);

  const res = await authedFetch("/api/leads", {
    method: "POST",
    body: JSON.stringify(lead),
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, 'Failed to save lead'));
  }
}

export async function deleteLeadFromFirestore(id: string): Promise<void> {
  const res = await authedFetch(`/api/leads/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, 'Failed to delete lead'));
  }

  const local = getLocalLeads();
  const filtered = local.filter(l => l.id !== id);
  saveLocalLeads(filtered);
}

// 2. User Management and Roles in PostgreSQL
export async function fetchCRMUsers(): Promise<CRMUser[]> {
  try {
    const res = await authedFetch("/api/users");
    if (!res.ok) {
      throw new Error(`Failed to fetch users: ${res.statusText}`);
    }
    const raw: CRMUser[] = await res.json();
    const users = raw.map(u => ({ ...u, role: normalizeRole(u.role) }));
    if (users.length > 0) {
      safeCache("crm_users_cache", users);
      return users;
    }
  } catch (error) {
    console.warn("Failed to fetch CRM users from PostgreSQL API:", error);
  }

  // Fallback
  const cached = localStorage.getItem("crm_users_cache");
  if (cached) return JSON.parse(cached);

  return [
    {
      uid: "admin-msterling",
      email: "msterling@armellini.com",
      displayName: "Mark Sterling",
      role: "admin",
      createdAt: new Date().toISOString()
    }
  ];
}

export async function saveCRMUser(user: CRMUser): Promise<void> {
  try {
    const res = await authedFetch("/api/users", {
      method: "POST",
      body: JSON.stringify(user),
    });
    if (!res.ok) {
      throw new Error(`Failed to save CRM user: ${res.statusText}`);
    }
  } catch (error) {
    console.warn("Failed to save CRM user to PostgreSQL API:", error);
  }

  // Cache user list locally
  const currentUsers = await fetchCRMUsers();
  const idx = currentUsers.findIndex(u => u.uid === user.uid);
  if (idx > -1) {
    currentUsers[idx] = user;
  } else {
    currentUsers.push(user);
  }
  safeCache("crm_users_cache", currentUsers);
}

export async function updateCRMUserRole(uid: string, role: UserRole): Promise<void> {
  try {
    const res = await authedFetch(`/api/users/${uid}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    });
    if (!res.ok) {
      throw new Error(`Failed to update user role: ${res.statusText}`);
    }
  } catch (error) {
    console.warn("Failed to update user role in PostgreSQL API:", error);
  }

  const cached = localStorage.getItem("crm_users_cache");
  if (cached) {
    const users = JSON.parse(cached) as CRMUser[];
    const idx = users.findIndex(u => u.uid === uid);
    if (idx > -1) {
      users[idx].role = role;
      safeCache("crm_users_cache", users);
    }
  }
}

// --- NON-FLORAL LTL LOGISTICS API CALLS ---

// 1. Shippers API
export async function fetchShippers(): Promise<Shipper[]> {
  try {
    const res = await authedFetch("/api/shippers");
    if (!res.ok) throw new Error("Failed to fetch shippers");
    const data = await res.json();
    safeCache("leadtracker_shippers", data);
    return data;
  } catch (error) {
    console.warn("API error fetching shippers, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_shippers");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveShipper(shipper: Shipper): Promise<void> {
  const res = await authedFetch("/api/shippers", {
    method: "POST",
    body: JSON.stringify(shipper)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save shipper"));
  }
  const cached = localStorage.getItem("leadtracker_shippers");
  const shippers: Shipper[] = cached ? JSON.parse(cached) : [];
  const idx = shippers.findIndex(s => s.shipperName === shipper.shipperName);
  if (idx > -1) shippers[idx] = shipper;
  else shippers.push(shipper);
  safeCache("leadtracker_shippers", shippers);
}

export async function deleteShipper(name: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/shippers/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete shipper");
  } catch (error) {
    console.warn("API error deleting shipper:", error);
  }
  const cached = localStorage.getItem("leadtracker_shippers");
  if (cached) {
    const shippers: Shipper[] = JSON.parse(cached);
    const filtered = shippers.filter(s => s.shipperName !== name);
    safeCache("leadtracker_shippers", filtered);
  }
}

// 2. Consignees API
export async function fetchConsignees(): Promise<Consignee[]> {
  try {
    const res = await authedFetch("/api/consignees");
    if (!res.ok) throw new Error("Failed to fetch consignees");
    const data = await res.json();
    safeCache("leadtracker_consignees", data);
    return data;
  } catch (error) {
    console.warn("API error fetching consignees, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_consignees");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveConsignee(consignee: Consignee): Promise<void> {
  const res = await authedFetch("/api/consignees", {
    method: "POST",
    body: JSON.stringify(consignee)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save consignee"));
  }
  const cached = localStorage.getItem("leadtracker_consignees");
  const consignees: Consignee[] = cached ? JSON.parse(cached) : [];
  const idx = consignees.findIndex(c => c.consigneeAccount === consignee.consigneeAccount);
  if (idx > -1) consignees[idx] = consignee;
  else consignees.push(consignee);
  safeCache("leadtracker_consignees", consignees);
}

export async function deleteConsignee(account: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/consignees/${encodeURIComponent(account)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete consignee");
  } catch (error) {
    console.warn("API error deleting consignee:", error);
  }
  const cached = localStorage.getItem("leadtracker_consignees");
  if (cached) {
    const consignees: Consignee[] = JSON.parse(cached);
    const filtered = consignees.filter(c => c.consigneeAccount !== account);
    safeCache("leadtracker_consignees", filtered);
  }
}

// 3. Bill-Tos API
export async function fetchBillTos(): Promise<BillTo[]> {
  try {
    const res = await authedFetch("/api/billtos");
    if (!res.ok) throw new Error("Failed to fetch billtos");
    const data = await res.json();
    safeCache("leadtracker_billtos", data);
    return data;
  } catch (error) {
    console.warn("API error fetching billtos, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_billtos");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveBillTo(billTo: BillTo): Promise<void> {
  const res = await authedFetch("/api/billtos", {
    method: "POST",
    body: JSON.stringify(billTo)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save billto"));
  }
  const cached = localStorage.getItem("leadtracker_billtos");
  const billTos: BillTo[] = cached ? JSON.parse(cached) : [];
  const idx = billTos.findIndex(b => b.billToAccount === billTo.billToAccount);
  if (idx > -1) billTos[idx] = billTo;
  else billTos.push(billTo);
  safeCache("leadtracker_billtos", billTos);
}

export async function deleteBillTo(account: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/billtos/${encodeURIComponent(account)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete billto");
  } catch (error) {
    console.warn("API error deleting billto:", error);
  }
  const cached = localStorage.getItem("leadtracker_billtos");
  if (cached) {
    const billTos: BillTo[] = JSON.parse(cached);
    const filtered = billTos.filter(b => b.billToAccount !== account);
    safeCache("leadtracker_billtos", filtered);
  }
}

// 4. Commodities API
export async function fetchCommodities(): Promise<Commodity[]> {
  try {
    const res = await authedFetch("/api/commodities");
    if (!res.ok) throw new Error("Failed to fetch commodities");
    const data = await res.json();
    safeCache("leadtracker_commodities", data);
    return data;
  } catch (error) {
    console.warn("API error fetching commodities, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_commodities");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveCommodity(commodity: Commodity): Promise<void> {
  const res = await authedFetch("/api/commodities", {
    method: "POST",
    body: JSON.stringify(commodity)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save commodity"));
  }
  const cached = localStorage.getItem("leadtracker_commodities");
  const commodities: Commodity[] = cached ? JSON.parse(cached) : [];
  const idx = commodities.findIndex(c => c.commodity === commodity.commodity);
  if (idx === -1) {
    commodities.push(commodity);
    safeCache("leadtracker_commodities", commodities);
  }
}

export async function deleteCommodity(name: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/commodities/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete commodity");
  } catch (error) {
    console.warn("API error deleting commodity:", error);
  }
  const cached = localStorage.getItem("leadtracker_commodities");
  if (cached) {
    const commodities: Commodity[] = JSON.parse(cached);
    const filtered = commodities.filter(c => c.commodity !== name);
    safeCache("leadtracker_commodities", filtered);
  }
}

// 5. Lara Emails Queue API
export async function fetchLaraEmails(): Promise<LaraEmail[]> {
  try {
    const res = await authedFetch("/api/lara-emails");
    if (!res.ok) throw new Error("Failed to fetch lara emails");
    const data = await res.json();
    safeCache("leadtracker_lara_emails", data);
    return data;
  } catch (error) {
    console.warn("API error fetching lara emails, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_lara_emails");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveLaraEmail(email: LaraEmail): Promise<void> {
  const res = await authedFetch("/api/lara-emails", {
    method: "POST",
    body: JSON.stringify(email)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save lara email"));
  }
  const cached = localStorage.getItem("leadtracker_lara_emails");
  const laraEmails: LaraEmail[] = cached ? JSON.parse(cached) : [];
  const idx = laraEmails.findIndex(e => e.id === email.id);
  if (idx > -1) laraEmails[idx] = email;
  else laraEmails.push(email);
  safeCache("leadtracker_lara_emails", laraEmails);
}

export async function deleteLaraEmail(id: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/lara-emails/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete lara email");
  } catch (error) {
    console.warn("API error deleting lara email:", error);
  }
  const cached = localStorage.getItem("leadtracker_lara_emails");
  if (cached) {
    const laraEmails: LaraEmail[] = JSON.parse(cached);
    const filtered = laraEmails.filter(e => e.id !== id);
    safeCache("leadtracker_lara_emails", filtered);
  }
}

// 6. NONLTL Shipments API
export async function fetchNonltlShipments(): Promise<NonltlShipment[]> {
  try {
    const res = await authedFetch("/api/nonltl-shipments");
    if (!res.ok) throw new Error("Failed to fetch shipments");
    const data = await res.json();
    safeCache("leadtracker_nonltl_shipments", data);
    return data;
  } catch (error) {
    console.warn("API error fetching nonltl shipments, loading from cache:", error);
    const cached = localStorage.getItem("leadtracker_nonltl_shipments");
    return cached ? JSON.parse(cached) : [];
  }
}

export async function saveNonltlShipment(shipment: NonltlShipment): Promise<void> {
  const res = await authedFetch("/api/nonltl-shipments", {
    method: "POST",
    body: JSON.stringify(shipment)
  });
  if (!res.ok) {
    throw new Error(await describeFailure(res, "Failed to save shipment"));
  }
  const cached = localStorage.getItem("leadtracker_nonltl_shipments");
  const shipments: NonltlShipment[] = cached ? JSON.parse(cached) : [];
  const idx = shipments.findIndex(s => s.quoteNumber === shipment.quoteNumber);
  if (idx > -1) shipments[idx] = shipment;
  else shipments.push(shipment);
  safeCache("leadtracker_nonltl_shipments", shipments);
}

export async function deleteNonltlShipment(quoteNumber: string): Promise<void> {
  try {
    const res = await authedFetch(`/api/nonltl-shipments/${quoteNumber}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("Failed to delete shipment");
  } catch (error) {
    console.warn("API error deleting shipment:", error);
  }
  const cached = localStorage.getItem("leadtracker_nonltl_shipments");
  if (cached) {
    const shipments: NonltlShipment[] = JSON.parse(cached);
    const filtered = shipments.filter(s => s.quoteNumber !== quoteNumber);
    safeCache("leadtracker_nonltl_shipments", filtered);
  }
}

// 6a0. Google Sheet sync for Non-Floral shipments (client reads the sheet,
// server upserts the rows)
export async function sheetSyncNonltl(rows: Record<string, string>[]): Promise<{ success: boolean; synced: number; skipped: number }> {
  const res = await authedFetch("/api/nonltl-shipments/sheet-sync", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Sheet sync failed");
  return data;
}



// 6b. Audit Log (read-only; written server-side on every save/delete)
export interface AuditEntry {
  id: string;
  timestamp: string;
  userEmail?: string | null;
  action: string;
  entity: string;
  entityId?: string | null;
  details?: string | null;
}

export async function fetchAuditLog(limit = 100): Promise<AuditEntry[]> {
  try {
    const res = await authedFetch(`/api/audit-log?limit=${limit}`);
    if (!res.ok) throw new Error("Failed to fetch audit log");
    return await res.json();
  } catch (error) {
    console.warn("API error fetching audit log:", error);
    return [];
  }
}

// 7. Tracked Emails (Gmail reply tracking)
export interface TrackedEmail {
  trackingId: string;
  leadId?: string | null;
  leadName?: string | null;
  threadId?: string | null;
  messageId?: string | null;
  recipient?: string | null;
  subject?: string | null;
  sentBy?: string | null;
  sentAt?: string | null;
  replyCount?: number | null;
  lastReplyAt?: string | null;
  lastReplyFrom?: string | null;
  lastReplySnippet?: string | null;
}

export async function fetchTrackedEmails(): Promise<TrackedEmail[]> {
  try {
    const res = await authedFetch("/api/tracked-emails");
    if (!res.ok) throw new Error("Failed to fetch tracked emails");
    return await res.json();
  } catch (error) {
    console.warn("API error fetching tracked emails:", error);
    return [];
  }
}

export async function saveTrackedEmail(row: TrackedEmail): Promise<void> {
  try {
    const res = await authedFetch("/api/tracked-emails", {
      method: "POST",
      body: JSON.stringify(row)
    });
    if (!res.ok) throw new Error("Failed to save tracked email");
  } catch (error) {
    console.warn("API error saving tracked email:", error);
  }
}

export async function fetchAIStatusRecommendation(nextSteps: string, currentStatus: string): Promise<{ recommendedStatus: string; reasoning: string }> {
  try {
    const res = await authedFetch("/api/leads/recommend-status", {
      method: "POST",
      body: JSON.stringify({ nextSteps, currentStatus }),
    });
    if (!res.ok) {
      throw new Error(`Failed to get recommendation: ${res.statusText}`);
    }
    return await res.json();
  } catch (error: any) {
    console.error("Error fetching AI status recommendation:", error);
    throw error;
  }
}

export async function fetchCData(view: string, filter?: string, orderby?: string): Promise<any> {
  // Direct Armellini Aelis 4 Accounts API (Primary Source of Truth)
  try {
    if (view === 'vwconsigneemasterai') {
      const res = await authedFetch('/api/aelis/accounts/consignees');
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list) && list.length > 0) {
          const value = list.map((item: any) => {
            const acct = item.accountNumber || item.consigneeAccount || item.consigneeId || item.number || item.Account_Number || String(item.id || '');
            const name = item.name || item.consigneeName || item.customerName || item.consigneeAccountName || item.Customer_Name || 'Consignee';
            const delInst = item.deliveryInstructions || item.Delivery_Instructions || item.deliveryInstructionsPlainText || '';
            const alarmInst = item.alarmInstructions || item.Alarm_Instructions || item.alarmInstructionsPlainText || '';
            const parentAcct = item.Parent_Account || item.parent_account || item.parentAccountNumber || item.parentId || item.parent_id || '';
            const a4id = item.a4consid || item.id || acct;

            return {
              id: item.id || acct,
              a4consid: a4id,
              Account_Number: acct,
              accountNumber: acct,
              consignee_account: acct,
              Customer_Name: name,
              consigneename: name,
              consigneeAccountName: name,
              name: name,
              City: item.city || item.consigneeCity || item.shippingCity || item.City || '',
              city: item.city || item.consigneeCity || item.shippingCity || item.City || '',
              state: item.state || item.consigneeState || item.shippingStateCode || '',
              Shipping_Address: item.address || item.consigneeAddress || item.shippingAddress || item.Shipping_Address || '',
              Phone: item.phone || item.consigneePhone || item.Phone || '',
              phone: item.phone || item.consigneePhone || item.Phone || '',
              email: item.email || item.consigneeEmail || '',
              PO_Required: item.poRequired ? 1 : 0,
              Onhold: item.onHold ? 1 : 0,
              status: item.status !== false ? 1 : 0,
              Parent_Account: parentAcct,
              deliveryInstructions: delInst,
              Delivery_Instructions: delInst,
              alarmInstructions: alarmInst,
              Alarm_Instructions: alarmInst,
              _source: 'Aelis4API'
            };
          });
          return { value };
        }
      }
    } else if (view === 'vwshippermaster_new') {
      const res = await authedFetch('/api/aelis/accounts/shippers');
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list) && list.length > 0) {
          const value = list.map((item: any) => ({
            shipper_account: item.accountNumber || item.shipperAccount || item.shipperId || item.number || String(item.id || ''),
            shippername: item.name || item.shipperName || 'Shipper',
            city: item.city || item.shipperCity || item.shippingCity || '',
            state: item.state || item.shipperState || item.shippingStateCode || '',
            address: item.address || item.shipperAddress || item.shippingAddress || '',
            phone: item.phone || item.shipperPhone || '',
            email: item.email || item.shipperEmail || '',
            porequired: item.poRequired ? 1 : 0,
            onhold: item.onHold ? 1 : 0,
            status: item.status !== false ? 1 : 0,
            Parent_Account: item.parentAccountNumber || item.parentId || '',
            _source: 'Aelis4API'
          }));
          return { value };
        }
      }
    } else if (view === 'vwaelis5DaysofService') {
      const res = await authedFetch('/api/aelis/accounts/days-of-service');
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list) && list.length > 0) {
          const value = list.map((item: any) => ({
            Account: item.accountNumber || item.account || '',
            Region: item.region || item.destinationRegion || 'FL',
            Date: item.date || item.serviceDate || new Date().toISOString().slice(0, 10),
            Warehouse: item.origin || item.warehouse || 'MIA',
            OutWarehouse: item.destination || item.outWarehouse || 'ORL',
            deliverywindowstart: item.deliveryWindowStart || '08:00',
            deliverywindowend: item.deliveryWindowEnd || '17:00',
            _source: 'Aelis4API'
          }));
          return { value };
        }
      }
    }
  } catch (err: any) {
    console.warn(`Direct Aelis 4 API directory fetch failed for ${view}:`, err.message);
  }

  // Fallback to local server proxy endpoint
  try {
    const params = new URLSearchParams();
    params.append("view", view);
    if (filter) params.append("filter", filter);
    if (orderby) params.append("orderby", orderby);
    
    const res = await authedFetch(`/api/cdata-proxy?${params.toString()}`);
    if (res.ok) {
      const data = await res.json();
      if (data && Array.isArray(data.value) && data.value.length > 0) {
        return data;
      }
    }
  } catch (error) {
    console.warn(`Server proxy fallback error for ${view}:`, error);
  }

  return { value: [] };
}

export interface UserPreferences {
  columns?: any[];
  filters?: any[];
  sort?: any[];
  smartViews?: any[];
  statusColors?: Record<string, string>;
}

export async function fetchUserPreferences(): Promise<UserPreferences> {
  try {
    const res = await authedFetch('/api/user-preferences');
    if (res.ok) {
      return (await res.json()) as UserPreferences;
    }
  } catch (err) {
    console.error('Failed to fetch user preferences:', err);
  }
  return {};
}

/** Saves a partial update; the server merges it with the stored sections. */
export async function saveUserPreferences(patch: UserPreferences): Promise<boolean> {
  try {
    const res = await authedFetch('/api/user-preferences', {
      method: 'POST',
      body: JSON.stringify(patch),
    });
    return res.ok;
  } catch (err) {
    console.error('Failed to save user preferences:', err);
    return false;
  }
}





